'use client';

import type { SchedulingAvailabilityInfo } from '@carvana/merch-contracts';
import { InventoryTypeKey } from '@carvana/merch-contracts';
import { FulfillmentTypeKey } from '@carvana/merch-contracts/FulfillmentType';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo, useRef } from 'react';
import useNewCarDeliveryPickup from '~/hooks/new/useNewCarDeliveryPickup';
import useNewCarPricing from '~/hooks/new/useNewCarPricing';
import useClosestVehicle from '~/hooks/new-cars/selectors/useClosestVehicle';
import type { VehiclePricingResponse } from '~/services/common/contracts/PricingResponse';
import type { DeepPartial } from '~/types';

type ContextType = {
  delivery: SchedulingAvailabilityInfo | undefined;
  isDeliveryLoaded: boolean;
  isPricingLoaded: boolean;
  isPricingLoading: boolean;
  pickup: SchedulingAvailabilityInfo | undefined;
  pricingData: Partial<VehiclePricingResponse>;
  soonestRoute: SchedulingAvailabilityInfo | undefined;
};

const defaultContext: ContextType = {
  delivery: undefined,
  isDeliveryLoaded: false,
  isPricingLoaded: false,
  isPricingLoading: false,
  pickup: undefined,
  pricingData: {},
  soonestRoute: undefined
};

const PriceBreakdownContext = createContext<ContextType>(defaultContext);

export function PriceBreakdownContextProvider({
  children
}: React.PropsWithChildren) {
  const closestVehicle = useClosestVehicle();

  const {
    fulfillmentType,
    isPurchasePending,
    locationId,
    vehicleId,
    vehicleInventoryType
  } = closestVehicle
    ? closestVehicle
    : {
        fulfillmentType: FulfillmentTypeKey.Core,
        isPurchasePending: false,
        locationId: 0,
        vehicleId: 0,
        vehicleInventoryType: InventoryTypeKey.Core
      };

  const isWip = vehicleInventoryType === InventoryTypeKey.WIP;
  const vehicleSaleStatus = isPurchasePending ? '' : 'Available';

  const {
    delivery,
    isLoaded: isDeliveryLoaded,
    isLoading: isDeliveryLoading,
    pickup,
    soonestRoute
  } = useNewCarDeliveryPickup(vehicleId, {
    skipAndReset: isWip,
    vehicleInventoryType: fulfillmentType,
    vehicleLocationId: locationId,
    vehicleSaleStatus
  });

  const vehicleIdRef = useRef(vehicleId);
  const vehicleIdChanged = vehicleIdRef.current !== vehicleId;
  vehicleIdRef.current = vehicleId;

  const {
    data: pricingData,
    isLoaded: isPricingLoaded,
    isLoading: isPricingLoading
  } = useNewCarPricing(vehicleId, {
    routeId: soonestRoute?.routeId,
    skip: vehicleIdChanged || !isDeliveryLoaded || isDeliveryLoading
  });

  const value = useMemo<ContextType>(
    () => ({
      delivery,
      isDeliveryLoaded,
      isPricingLoaded,
      isPricingLoading,
      pickup,
      pricingData,
      soonestRoute
    }),
    [
      delivery,
      isDeliveryLoaded,
      isPricingLoaded,
      isPricingLoading,
      pickup,
      pricingData,
      soonestRoute
    ]
  );

  return (
    <PriceBreakdownContext.Provider value={value}>
      {children}
    </PriceBreakdownContext.Provider>
  );
}

PriceBreakdownContextProvider.displayName = 'PriceBreakdownContextProvider';

export function usePriceBreakdownContext(): ContextType {
  return useContext(PriceBreakdownContext);
}

export function generatePriceBreakdownContext(
  customState: DeepPartial<ContextType>
): ContextType {
  return deepmerge<ContextType, DeepPartial<ContextType>>(
    defaultContext,
    customState
  );
}

export function MockedPriceBreakdownContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <PriceBreakdownContext.Provider
      value={generatePriceBreakdownContext(mockValue)}
    >
      {children}
    </PriceBreakdownContext.Provider>
  );
}

export default PriceBreakdownContext;
